import http from '@/http';

const LOCALES_RECEIVED = 'LOCALES_RECEIVED';

const state = {
  items: [],
  isLoaded: false
};

const getters = {
  validLocaleCodesSet(state) {
    return new Set([ ...state.items.map(it => it.code4) ])
  }
}

const actions = {
  async getLocales({ commit }) {
    try {
      if (!state.isLoaded) {
        const {data} = await http.get('locales');
        commit(LOCALES_RECEIVED, {locales: data});
      }
    } catch (err) {
      // na
    }
  }
};

const mutations = {
  [LOCALES_RECEIVED](state, { locales }) {
    state.items = locales;
    state.isLoaded = true;
  }
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};

export function sortLocales(locales, { userLocale, tenantLocale } = {}) {
  const standardize = s => (s || '').trim().toLowerCase()
  const codesEqual = (a, b) => standardize(a) === standardize(b)
  const languageRoot = s => standardize(s).split('-')[0]
  const getName = l => (l.name || l.code4 || '').toString()

  const userCode = standardize(userLocale)
  const userRoot = languageRoot(userCode)
  const tenantCode = standardize(tenantLocale)
  const tenantRoot = languageRoot(tenantCode)

  const group = (locale) => {
    const code = standardize(locale.code4);
    const root = languageRoot(code);

    // 1) exact user locale
    if (userCode && codesEqual(code, userCode)) return 0;

    // 2) user-related by root (exclude exact)
    if (userRoot && root === userRoot) return 1;

    // 3) exact tenant locale
    if (tenantCode && codesEqual(code, tenantCode)) return 2;

    // 4) tenant-related by root (exclude exact)
    if (tenantRoot && root === tenantRoot) return 3;

    // 5) English block (if not already captured above)
    if (codesEqual(code, 'en-US')) return 4
    if (root === 'en') return 5

    // 6) everything else
    return 6;
  };

  const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: false });

  return [...locales].sort((a, b) => {
    const ga = group(a), gb = group(b);
    if (ga !== gb) return ga - gb;

    // Within groups, sort by language name (case/diacritic-insensitive), then by code
    const na = getName(a), nb = getName(b);
    const byName = collator.compare(na, nb);
    if (byName !== 0) return byName;

    return collator.compare(norm(a.code4), norm(b.code4));
  });
}

export function getFallbackLocale(locales, userLocale, tenantLocale) {
  const standardize = s => (s || '').toString().trim()

  const localeList = (locales || [])
    .filter(l => l && l.code4)
    .map(l => ({
      loc: l,
      localeCode: standardize(l.code4),
      root: l.code2 == null ? '' : String(l.code2).trim()
    }))

  const result = new Set()
  const user = standardize(userLocale)
  const tenant = standardize(tenantLocale)
  const userRoot = user.split('-')[0]
  const tenantRoot = tenant.split('-')[0]

  // 1) Fallbacks from same language family as userLocale (excluding user)
  if (userRoot) {
    localeList
      .filter(l =>
        l.localeCode !== user &&
        l.localeCode.startsWith(userRoot + '-') &&
        l.root !== ''
      )
      .forEach(l => result.add(l.loc.code4))
  }

  // 2) Tenant Default
  if (tenant && tenant !== user && !result.has(tenant)) {
    result.add(tenant)
  }
  // 3) Fallbacks from tenant’s language family (excluding user)
  if (tenantRoot && userRoot !== tenantRoot) {
    localeList
      .filter(l =>
        l.localeCode !== user &&
        l.localeCode.startsWith(tenantRoot + '-') &&
        l.root !== ''
      )
      .forEach(l => result.add(l.loc.code4))
  }
  // 4) Always include 'en-US' last (if not already included)
  if (userLocale !== 'en-US' && !result.has('en-US')) {
    result.add('en-US')
  }

  // Return unique fallbacks in insertion order
  return Array.from(result)
}
